import metaDataMapping from "../../../packages/graphql-layer/src/__downloaded__/metadata.json";

/**
 * This file was more or less copied from CM's Spark project.
 */

interface Dispatchable {
    __typename?: string;
    viewtype?: string | null;
}

type metadata = { "data-cm-metadata": string } | undefined;

export interface MetadataRoot {
    id?: string | null;
    type?: string;
}

export interface PreviewMetadataProps<T> {
    properties?: { [P in keyof Omit<T, "metadata">]?: string };
    root: MetadataRoot;
}

export interface PreviewMetadata {
    metadata?: PreviewMetadataProps<this>;
}

export const initializeMetadata = (id: string | undefined, type = "content") => {
    const metaDataRoot = {id: id, type: type} as MetadataRoot;
    return metaDataElement(true, metaDataRoot);
};

export const createMetaDataRoot = (id: string) => {
    return {id: id, type: 'content'} as MetadataRoot;
}


export const metaDataElementWithId = (isPreview: boolean, id: string | undefined): metadata => {
    if (!isPreview || !id) {
        return undefined;
    }
    const metadata = createMetaDataRoot(id);
    return metaDataElement(isPreview, metadata);
}

/**
 * Generates the CoreMedia metadata JSON with id.
 * @param isPreview true if we are on a preview page
 * @param metadataRoot Id of the given content
 * @param propertyNames List of optional properties
 */
export const metaDataElement = (isPreview: boolean,
                                metadataRoot: MetadataRoot | undefined,
                                ...propertyNames: Array<string | undefined>
): metadata => {
    if (!isPreview || !metadataRoot) {
        return undefined;
    }
    const metadata = [];

    metadata.push({_: {$Ref: (metadataRoot.type || "content") + "/" + metadataRoot.id}});

    if (propertyNames) {
        propertyNames.forEach((property) => {
            if (property) {
                metadata.push({
                    _: property,
                });
                metadata.push({editable: true});
            }
        });
    }
    return {"data-cm-metadata": JSON.stringify(metadata)};
};

/**
 * Generates the CoreMedia metadata JSON with a property.
 * @param isPreview true if we are on a preview page
 * @param property The given property of a content
 * @param contentId
 */
export const metaDataProperty = (isPreview: boolean, property: string | undefined, contentId?: string): metadata => {
    if (!isPreview || !property || !contentId) {
        return undefined;
    }

    const metadata = [];
    contentId && metadata.push({_: {$Ref: ("content") + "/" + contentId}});
    metadata.push({_: property});
    metadata.push({editable: true});
    return {"data-cm-metadata": JSON.stringify(metadata)};
};

/**
 * Generates the CoreMedia metadata JSON for a [[PageGridPlacement]].
 * @param isPreview true if we are on a preview page
 * @param name The name of the placement
 * @param hasItems Wether this placement has items or not
 */
export const metaDataForPlacement = (isPreview: boolean, name: string, hasItems?: boolean): metadata => {
    if (!isPreview) {
        return undefined;
    }
    const metadata = [
        {_: `properties.placement-${name}`},
        {
            placementRequest: [{isInLayout: true, hasItems: hasItems || false, placementName: name}],
        },
    ];
    return {"data-cm-metadata": JSON.stringify(metadata)};
};

/**
 * Generates the CoreMedia metadata JSON including the responsive device settings.
 * @param isPreview true if we are on a preview page
 */
export const metaDataForResponsiveDevices = (isPreview: boolean): metadata => {
    if (!isPreview) {
        return undefined;
    }

    const metadata = [
        {
            cm_responsiveDevices: {
                mobile_portrait: {width: 414, height: 736, order: 1},
                mobile_landscape: {width: 736, height: 414, order: 2},
                tablet_portrait: {width: 768, height: 1024, order: 3},
                tablet_landscape: {width: 1024, height: 768, order: 4, isDefault: "true"},
            },
            cm_preferredWidth: 1200,
        },
    ];

    return {"data-cm-metadata": JSON.stringify(metadata)};
};

/**
 * Generates the CoreMedia metadata JSON for Studio specific CSS and JS.
 * Should be attached to the head element.
 * @param isPreview
 * @param cssUrls
 * @param jsUrls
 */
export const metaDataForStudioPreviewResources = (isPreview: boolean, cssUrls?: string[], jsUrls?: string[]): metadata => {
    if (!isPreview || (!cssUrls && !jsUrls)) {
        return undefined;
    }

    const metadata: any = {};
    if (cssUrls) {
        metadata.cm_studioPreviewCss = cssUrls;
    }
    if (jsUrls) {
        metadata.cm_studioPreviewJs = jsUrls;
    }

    return {"data-cm-metadata": JSON.stringify([metadata])};
}

/**
 * Generates the CoreMedia metadata JSON for highlight strategy.
 * @param isPreview
 * @param strategy
 */
export const metaDataHighlightStrategy = (isPreview: boolean, strategy: "CSS" | "none" = "CSS"): metadata => {
    if (!isPreview) {
        return undefined;
    }
    return {"data-cm-metadata": JSON.stringify([{cm_highlightStrategy: strategy}])};
}

/**
 * Helper to get the property name from metaDataMapping for a given content type,
 * falls back to the given propertyName if no mapping is found.
 * @param type The given content object
 * @param propertyName The name of the property
 */
export function getPropertyName<S extends Dispatchable>(type: S, propertyName: keyof S): string {
    let property = "properties." + (propertyName as string);
    const mapping: { [key: string]: any } = metaDataMapping;
    if (type.__typename) {
        const metaDataMappingElement: { [key: string]: string } = mapping[type.__typename];
        property = (metaDataMappingElement && metaDataMappingElement[propertyName as string]) || (propertyName as string);
    }
    return property;
}